import { NetworkStatus, useQuery } from '@apollo/client';
import { Query } from 'graphql/generated/graphqlTypes';
import { GET_DOORS_FOR_TENANT, GET_LOCATIONS_WITH_DOORS_FOR_TENANT } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useMemo } from 'react';
import { stitchDoorLocations } from 'v2/utils/lock';
import { TranslatedHookProps } from '../hooks.types';

const useDevices = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const {
    loading: doorsLoading,
    error: doorsError,
    data: doors,
    networkStatus: doorsNetworkStatus
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_DOORS_FOR_TENANT, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const {
    loading: locationsLoading,
    error: locationsError,
    data: locations,
    networkStatus: locationsNetworkStatus
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_LOCATIONS_WITH_DOORS_FOR_TENANT, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const devicesLoading = (doorsLoading && !doors) || (locationsLoading && !locations);

  const isRefetchingDevices =
    !devicesLoading &&
    ((!!doors && doorsNetworkStatus !== NetworkStatus.ready) ||
      (!!locations && locationsNetworkStatus !== NetworkStatus.ready));

  useRegisterBackgroundRefetch(isRefetchingDevices);

  const devices = useMemo(
    () => stitchDoorLocations(doors?.getDoorsForTenant ?? [], locations?.getLocationsForTenant ?? []),
    [doors, locations]
  );

  return {
    devices,
    devicesLoading,
    isRefetchingDevices,
    lockGroups: locations?.getLocationsForTenant || [],
    lockGroupsLoading: locationsLoading && !locations
  };
};

export default useDevices;
